import type { AccessRuleInputData } from '@sunstone/api-client/hooks';
import { useListAccessibleWorkspaces } from '@sunstone/api-client/hooks';
import { Checkbox, Input, Label } from '@sunstone/ui';
import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuthToken } from '@/features/auth';
import { useDebounce } from '@/hooks/useDebounce';

interface WorkspaceItem {
  id: string;
  displayName: string;
}

interface WorkspacePickerProps {
  tenantId: string;
  /** Scopes to a partner's descendant tenants instead of tenantId — a
   * partner owns no workspaces directly, only its child tenants do.
   * Mutually exclusive with tenantId (pass tenantId='' when set). */
  parentTenantId?: string;
  value: string[];
  onChange: (ids: string[]) => void;
  search: string;
  onSearchChange: (search: string) => void;
  /** The outer (role) and inner (user) access-rule envelopes to scope this
   * picker's rows to (accessrule.gateBoth) — may contain both workspace-kind
   * and deployment-kind rows; a deployment-kind row gates a workspace by its
   * parent deployment even though ListAccessibleWorkspaces never names a
   * deploymentId (accessrule.decide's parent cascade, applied server-side).
   * Undefined/empty means no candidate rules at all, i.e. every workspace in
   * scope. */
  roleRules?: AccessRuleInputData[];
  userRules?: AccessRuleInputData[];
}

/**
 * Flat, searchable, paginated workspace picker — the "Specific workspaces"
 * mode of the Workspaces section. The deployment→workspace parent gate
 * (SUN-408 strict-intersection) is applied server-side by
 * ListAccessibleWorkspaces via roleRules/userRules, so this is a plain flat
 * list — same shell as DeploymentPicker, no per-deployment grouping.
 */
export function WorkspacePicker({
  tenantId,
  parentTenantId,
  value,
  onChange,
  search,
  onSearchChange,
  roleRules,
  userRules,
}: WorkspacePickerProps) {
  const { token } = useAuthToken();
  const debouncedSearch = useDebounce(search);
  const [page, setPage] = useState(1);
  const [loaded, setLoaded] = useState<WorkspaceItem[]>([]);
  const { data } = useListAccessibleWorkspaces(token, tenantId, parentTenantId, {
    search: debouncedSearch,
    pageSize: 100,
    page,
    roleRules,
    userRules,
  });
  const totalCount = data?.totalCount ?? 0;
  const selected = new Set(value);

  // biome-ignore lint/correctness/useExhaustiveDependencies: debouncedSearch is a trigger dep — the effect resets accumulation whenever it changes, without referencing its value in the body.
  useEffect(() => {
    setPage(1);
    setLoaded([]);
  }, [debouncedSearch]);

  useEffect(() => {
    const fetched = data?.workspaces ?? [];
    if (fetched.length === 0) return;
    setLoaded((prev) => {
      const merged = [...prev];
      for (const ws of fetched) {
        if (!merged.some((m) => m.id === ws.id))
          merged.push({ id: ws.id, displayName: ws.displayName });
      }
      return merged;
    });
  }, [data]);

  function toggle(id: string) {
    onChange(selected.has(id) ? value.filter((v) => v !== id) : [...value, id]);
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search workspaces…"
          className="pl-8 h-8 text-sm"
        />
      </div>
      {value.length > 0 && (
        <p className="text-xs text-muted-foreground px-1">{value.length} selected</p>
      )}
      {loaded.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">
          {debouncedSearch ? 'No matching workspaces.' : 'No workspaces are currently accessible.'}
        </p>
      ) : (
        <div className="rounded-md border max-h-64 overflow-y-auto divide-y">
          {loaded.map((ws) => (
            <div key={ws.id} className="flex items-center gap-2 py-1.5 px-3">
              <Checkbox
                id={`workspace-${ws.id}`}
                checked={selected.has(ws.id)}
                onCheckedChange={() => toggle(ws.id)}
              />
              <Label
                htmlFor={`workspace-${ws.id}`}
                className="text-sm cursor-pointer flex-1 truncate"
              >
                {ws.displayName}
              </Label>
            </div>
          ))}
        </div>
      )}
      {totalCount > loaded.length && (
        <button
          type="button"
          className="text-xs text-primary hover:underline px-1"
          onClick={() => setPage((p) => p + 1)}
        >
          Load more ({loaded.length} of {totalCount})
        </button>
      )}
    </div>
  );
}
